import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, RefreshCw, Wrench } from "lucide-react";

/**
 * HealthDiagnostics
 * Pannello che mostra le issues operative del tenant rilevate da GET /api/health/diagnostics.
 * Ogni issue ha severity (critical/warning/info), title, details, fix (testo) e — per ghost_orders —
 * un pulsante azione che chiama POST /api/health/cleanup-ghost-orders.
 */
const SEV_ICON = { critical: AlertOctagon, warning: AlertTriangle, info: Info };
const SEV_COLOR = {
    critical: "border-red-500/50 bg-red-900/15 text-red-200",
    warning: "border-amber-500/50 bg-amber-900/15 text-amber-200",
    info: "border-cyan-500/40 bg-cyan-900/15 text-cyan-200",
};

const HealthDiagnostics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cleaning, setCleaning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/health/diagnostics");
            setData(d);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const cleanupGhost = async () => {
        if (!window.confirm("Resettare archived_at sugli ordini fantasma? Operazione sicura: tocca solo gli ordini con status ancora attivo.")) return;
        setCleaning(true);
        try {
            const { data: r } = await api.post("/health/cleanup-ghost-orders");
            toast.success(`Puliti ${r.cleaned} ordini fantasma`);
            load();
        } catch (e) { toast.error(formatError(e)); }
        finally { setCleaning(false); }
    };

    const allClean = data && data.issues_count === 0;

    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-5" data-testid="health-panel">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Diagnostica sistema</div>
                    <h2 className="font-display text-lg font-bold leading-tight">Stato operativo</h2>
                    <p className="text-xs text-elegant mt-1 max-w-md">
                        Rileva ordini fantasma, stazioni mal configurate, tavoli bloccati e categorie duplicate.
                    </p>
                </div>
                <button onClick={load} disabled={loading} data-testid="health-refresh"
                    className="h-10 px-3 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center gap-2 text-xs uppercase tracking-wider font-bold disabled:opacity-40">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Verifica
                </button>
            </div>

            {!data && (
                <div className="text-xs text-elegant italic">Caricamento…</div>
            )}

            {allClean && (
                <div className="bg-deep border border-green-700/40 rounded-md p-4 flex items-center gap-3" data-testid="health-clean">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                        <div className="font-bold text-green-300 text-sm">Tutto in ordine</div>
                        <div className="text-[11px] text-elegant">Nessuna anomalia rilevata. Sistema in salute.</div>
                    </div>
                </div>
            )}

            {data && data.issues_count > 0 && (
                <div className="space-y-2" data-testid="health-issues">
                    {data.issues.map((iss, idx) => {
                        const Icon = SEV_ICON[iss.severity] || Info;
                        const cls = SEV_COLOR[iss.severity] || SEV_COLOR.info;
                        return (
                            <div key={`${iss.code}-${idx}`} className={`border rounded-md p-3 ${cls}`} data-testid={`health-issue-${iss.code}`}>
                                <div className="flex items-start gap-3">
                                    <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm">{iss.title}</div>
                                        <div className="text-[11px] opacity-80 mt-0.5">{iss.details}</div>
                                        {iss.fix && (
                                            <div className="text-[11px] mt-1.5 italic opacity-90">
                                                💡 {iss.fix}
                                            </div>
                                        )}
                                        {iss.code === "ghost_orders" && (
                                            <button
                                                onClick={cleanupGhost}
                                                disabled={cleaning}
                                                data-testid="health-cleanup-ghost"
                                                className="mt-3 h-9 px-3 bg-red-500/20 border border-red-500 text-red-100 hover:bg-red-500/30 rounded text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50"
                                            >
                                                <Wrench className="w-3 h-3" /> {cleaning ? "Pulizia in corso…" : "Ripulisci ordini fantasma"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {data && data.checked_at && (
                <div className="text-[10px] text-elegant italic mt-4 opacity-70">
                    Ultimo check: {new Date(data.checked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" })}
                </div>
            )}
        </div>
    );
};

export default HealthDiagnostics;
